// Display formatters + aging logic. The currency/date helpers are carried over
// from the scaffold's App.tsx (correct date-only parsing) per app.md §3/§8.

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function formatCurrency(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') return '—'
  const n = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(n)) return '—'
  return usd.format(n)
}

export function formatDate(value: string | undefined | null): string {
  if (!value) return '—'
  // A date-only value: parse the parts directly so the display never shifts a
  // day on a timezone conversion.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

/** MM/DD/YYYY — matches what the native date inputs display. */
export function formatNumericDate(value: string | undefined | null): string {
  if (!value) return '—'
  // A date-only value: parse the parts directly so the display never shifts a
  // day on a timezone conversion.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

export function formatDateTime(value: string | undefined | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Relative age of a timestamp ("just now", "5 min ago", "3 hours ago",
    "2 days ago"), falling back to an absolute date-time past a week. Rendered
    at load; a thread reload refreshes it. */
export function formatRelativeTime(value: string | undefined | null, now: Date = new Date()): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const sec = Math.round((now.getTime() - d.getTime()) / 1000)
  if (sec < 45) return 'just now'
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} min ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`
  const day = Math.round(hr / 24)
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`
  return formatDateTime(value)
}

export function toNumber(value: number | string | undefined | null): number {
  if (value === undefined || value === null || value === '') return 0
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isNaN(n) ? 0 : n
}

// ---- Invoice amount reconciliation -----------------------------------------
// One definition, used by the Import Control release gate, its auto-resolution
// of LineItemsToTotalMismatch, and the read-only line items footer at every later
// step. They ran three copies of this arithmetic and must not drift.
//
// Line items are TAX-INCLUSIVE: each line's amount is what gets posted to its
// GL account and already carries that line's share of the invoice's sales tax
// (`ai/current - two flows/ai.md` → Sales tax, 2026-07-29). So the lines are
// checked against the invoice TOTAL, not the pre-tax subtotal — the old
// comparison let a taxed invoice release with pre-tax lines and export short by
// exactly its tax (`qa/2026-07-29-mock-qa/REPORT.md` Q1). Subtotal and tax stay
// as printed on the document and are still checked against each other, so a
// reviewer can tick the header against the paper.
export type AmountCheck = {
  /** Σ line items = invoice total (the lines carry the tax). */
  lineItemsMatchTotal: boolean
  /** subtotal + tax = total — the document's own header arithmetic. */
  headerAddsUp: boolean
  /** Both. What the release gate requires. */
  reconciles: boolean
}

export function checkAmounts(
  lineItemsSum: number,
  subtotal: number | string | undefined | null,
  tax: number | string | undefined | null,
  total: number | string | undefined | null,
): AmountCheck {
  const lineItemsMatchTotal = Math.abs(lineItemsSum - toNumber(total)) <= 0.01
  const headerAddsUp = Math.abs(toNumber(subtotal) + toNumber(tax) - toNumber(total)) <= 0.01
  return { lineItemsMatchTotal, headerAddsUp, reconciles: lineItemsMatchTotal && headerAddsUp }
}

// ---- Aging (app.md §8) -----------------------------------------------------
// Age = now − invoice_date (invoice date). Color scale over 0–7 days:
// neutral 0–2, yellow 3–4, orange 5–6, red 7+. Always paired with the day count.
export type AgeBand = 'neutral' | 'yellow' | 'orange' | 'red'

export function ageInDays(invoiceDate: string | undefined | null, now: Date = new Date()): number | null {
  if (!invoiceDate) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(invoiceDate)
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(invoiceDate)
  if (Number.isNaN(d.getTime())) return null
  // Count calendar days between the two local dates. Diff the UTC-projected
  // y/m/d of each endpoint so a DST shift in the span can't drop or add a day.
  const startUtc = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  const nowUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.max(0, Math.round((nowUtc - startUtc) / 86_400_000))
}

export function ageBand(days: number | null): AgeBand {
  if (days === null) return 'neutral'
  if (days >= 7) return 'red'
  if (days >= 5) return 'orange'
  if (days >= 3) return 'yellow'
  return 'neutral'
}

/**
 * How a line's GL coding reads where the cell is not editable — the approval
 * tiers, Closing Confirmation, Denial Review, the archives.
 *
 * Fully coded  → the account name.
 * Half coded   → the expense type plus an explicit "awaiting cost center".
 * Uncoded      → an em dash.
 *
 * THE MIDDLE CASE IS THE POINT. `account_name` is null while a line is half
 * coded, so rendering it alone made a line that IS coded — it just has no
 * department yet — look identical to one nobody has touched, at exactly the
 * steps whose job is to tell them apart.
 *
 * `expenseTypeLabel` is the caller's resolved label — `useHalfLabels().expenseType`,
 * which every calling component already holds. Pass it: the fully coded case shows
 * `HSKG UNIFORMS` and the half-coded case beside it showed `67-506`, so the surface
 * that exists to make half-coded lines legible spoke in codes. It is optional only
 * so a caller with no master read still gets the code rather than a blank, which is
 * the same fallback the hook itself makes.
 */
export function lineItemCodingLabel(
  lineItem: {
    account_name?: string | null
    final_account_id?: string | null
    expense_type_code?: string | null
  },
  expenseTypeLabel?: string,
): string {
  if (lineItem.account_name) return lineItem.account_name
  if (lineItem.final_account_id) return lineItem.final_account_id
  if (lineItem.expense_type_code) {
    return `${expenseTypeLabel ?? lineItem.expense_type_code} — awaiting cost center`
  }
  return '—'
}
